import { Defaults } from 'es-decorators'
import { View } from 'es6/components/view'
import { KEY_CODES } from 'es6/constants'
import { CartItem } from 'es6/models/cart-item'
import { getCookie } from 'helpers/cookie-helper'
import { getRussianDeclension } from 'helpers/declination-helper'
import { supportsCssProperty } from 'helpers/html-helper'
import { getTemplate } from 'helpers/template-helper'
import { to } from 'helpers/url-helpers'
import Backbone from 'init/backbone'
import { isIE11 } from 'init/browser-check'
import { currentCurrency } from 'init/current-currency'
import { isDesktop } from 'init/is-desktop'
import { isMobile } from 'init/is-mobile'
import $ from 'init/jquery'
import isEqual from 'lodash/isEqual'
import isFunction from 'lodash/isFunction'
import isNumber from 'lodash/isNumber'
import isUndefined from 'lodash/isUndefined'
import { Item } from 'es6/models/item'
import { jsonFeatures } from 'init/json-features'
import { entryScriptName } from 'init/entry-script-name'
import { handleFieldInput } from 'helpers/input-helper'

/**
 * Вид для блока с количеством и кнопкой "в корзине" для товара в каталоге
 */
@Defaults({
  events: {
    'click [data-cart-link], [data-item-inc]': 'increaseQty',
    'click [data-item-dec]': 'decreaseQty',
    'click .set-to-zero': 'removeFromCart',
    'keyup [data-cart-input]': 'inputKeyUp',
    'change [data-cart-input]': 'inputChange',
    'mouseover [data-cart-link]': 'setPlaceholder',
    'mouseleave [data-cart-link]': 'removePlaceholder',
    'focusout [data-cart-input]': 'handleFocusOut',
    'click .js-add-to-min-real-qty': 'addToMinRealQty',
  },
  model: null,
  isNotCart: null,
  access: null,
  atAddToCartProcess: false,
  MAX_QTY_IN_INPUT: 99999,
  // Минимальное количество товара в розничной фасовке
  MIN_QTY_RETAIL: 1,
  addToCartBtnClassName: 'button_add2cart',
  qtyRuleClassName: 'qty-rule',
  additionalOptions: ['item'],
})
export class ItemQtyView extends View {
  /**
   * @inheritDoc
   */
  beforeRun () {
    let tooltipTemplates = {
      tooltip_template: '#item-tooltip',
      decrease_tooltip_template: '#decrease-item-tooltip',
      minimum_tooltip_text_template: '#item-tooltip-minimum-text',
      maximum_tooltip_text_template: '#item-tooltip-maximum-text',
      already_maximum_tooltip_text_template: '#item-tooltip-already-maximum-text',
      multiplicity_tooltip_text_template: '#item-tooltip-multiplicity-text',
      delete_tooltip_template: '#delete-item-tooltip',
      null_tooltip_template: '#null-item-tooltip',
      lessMinQtyTitleTemplate: '#less-min-qty-title-template',
      lessMinQtyWrapTemplate: '#less-min-qty-title-wrap-template',
    }
    this.qtyIsWeight = this.item.get('is_weighted_goods')
    this.isInRecommendationsBlock = this.$el.parent().hasClass('recommendations-item')
    Object.entries(tooltipTemplates).forEach(([propName, selector]) => {
      this[propName] = getTemplate(selector)
    })
    this.listenTo(Backbone, 'update-cart-item', this.updateOnSync)
    this.listenTo(Backbone, 'retail-info-received', this.onRetailFeeInfoReceived)
  }

  /**
   * @inheritDoc
   * @param {Object} options
   */
  run (options) {
    Object.assign(this, options)
  }

  /**
   * @inheritDoc
   */
  afterRun () {
    // Отправляем ошибку о совпадении старой и новой цены в sentry
    if (this._isPriceEqualPriceMax()) {
      const sid = this.item.get('sid') || this.model.get('item_sid')
      if (typeof Raven !== 'undefined' && typeof Raven.captureException === 'function') {
        Raven.captureException(new Error(`Товар, арт: ${sid}, имеет одинаковые старую и новую цены.`))
      }
    }
  }

  /**
   * Определяем максимальное кол-во товара доступное для заказа в каталоге
   * @returns {number|boolean} кол-во товара, либо false
   */
  _initMaximum () {
    return this.item.get('isEnough') ? false : this.item.get('balance')
  }

  /**
   * Определяет совпадают ли старая и новая цены.
   * @returns {*|boolean} true если совпадают и текущая валюта - рубли.
   * @private
   */
  _isPriceEqualPriceMax () {
    if (currentCurrency.id === 1) {
      let price,
        priceMax
      if (this.model.get('price') && this.model.get('price_max')) {
        price = this.model.get('price')
        priceMax = this.model.get('price_max')
      }
      if (this.item.get('price') && this.item.get('price_max')) {
        price = this.item.get('price')
        priceMax = this.item.get('price_max')
      }
      return (this.item.get('has_discount') && price && priceMax && (1 - price / priceMax) * 100 <= 0.5)
    }
    return false
  }

  /**
   * Обновление вью, если количество товара изменилось
   * @param {Object} model
   * @param {string} type
   */
  updateOnSync (model, type) {
    if (isEqual(this.item.get('id'), model.get('item_id'))) {
      this.setModel(model, type)
      Backbone.trigger('updated-modifier-count', this.item, this.model.toJSON())
      switch (type) {
        case 'update':
          this.render({ controls: [false, true] })
          break
        case 'create':
          this.render({ controls: [true, true] })
          break
        case 'delete':
          if (this.isCart) {
            this.trigger('remove-cart-item')
          }
          this.render({ controls: [false, true] })
      }
      this.trigger('update-on-sync', this.item)
    }
    this.atAddToCartProcess = false
    this.item.isSync = false
  }

  /**
   * Рендер наценки на сборку.
   */
  renderRetailFeeMarkup () {
    const retailFeeMarkup = this.item.get('retail_fee_markup')
    const hasRetailMarkup = Boolean(retailFeeMarkup && retailFeeMarkup.templates)
    if (hasRetailMarkup && this.model.get('is_balanced')) {
      let countLabel = retailFeeMarkup.count_label
      if (retailFeeMarkup.is_added_to_cart) {
        countLabel = this.lessMinQtyTitleTemplate({ countLabel })
      }
      const html = retailFeeMarkup.templates.lite_title.replace('{count_label}', countLabel)
      if (isMobile) {
        this.$el.addClass('with-less-min-qty')
      }
      this.removeQtyRule()
      this.$('.js-less-min-qty').html(
        this.lessMinQtyWrapTemplate({
          html: html,
          isAdded: retailFeeMarkup.is_added_to_cart,
        })
      )
    } else {
      const $qtyRule = this.$(`.${this.qtyRuleClassName}`)
      if (this.getQtyRule && $qtyRule.length) {
        $qtyRule.html(this.getQtyRule())
      }
    }
    this.$el.toggleClass('with-retail-fee-markup', hasRetailMarkup && retailFeeMarkup.is_added_to_cart)
  }

  /**
   * Удаляет блок с кол-вом покладки в корзину.
   */
  removeQtyRule () {
    this.$('.qty-rule').remove()
  }

  /**
   * Добавляем свой объект `CartItem` в коллекцию `app.cart` если ещё его в ней нет.
   */
  addCartItemToCart () {
    if (this.cart.get(this.model.get('id'))) {
      this.model = this.cart.get(this.model.get('id'))
      return
    }
    if (isNumber(this.model.get('qty'))) {
      this.cart.add(this.model, { silent: true })
    }
  }

  /**
   * Добавление до минимального количества из модели.
   */
  addToMinRealQty () {
    this.value = this.item.get('real_min_qty')
    this.addToCartPrev()
  }

  /**
   * Установка модели для представления.
   * @param {Model} model Модель.
   * @param {string} type Тип действия.
   */
  setModel (model, type) {
    const newItem = model.get('item')
    if (newItem) {
      if (newItem.hasOwnProperty('retail_fee_markup')) {
        this.item.set('retail_fee_markup', newItem.retail_fee_markup)
      } else if (newItem instanceof Item && !isUndefined(newItem.get('retail_fee_markup'))) {
        this.item.set('retail_fee_markup', newItem.get('retail_fee_markup'))
      }
    }
    if (type === 'delete') {
      this.item.set('retail_fee_markup', null)
      this.model = new CartItem(null, { item: this.item })
    } else {
      this.model = model
      this.model.set({ item: this.item })
    }
    if (!this.isCart) {
      this.item.set('cart_item', this.model.toJSON())
    } else {
      this.model.set({ item: this.item.toJSON() })
    }
  }

  /**
   * Вырезаем в поле ввода любые символы кроме цифр.
   * @param {event} event
   */
  inputKeyUp (event) {
    if (this.qtyIsWeight) {
      handleFieldInput(event, 'sum', true, 1, true)
    } else {
      const value = this.input.val()
      const validValue = value.replace(/([^0-9])/g, '')
      if (validValue.length !== value.length) {
        this.input.val(validValue)
      }
    }
    if (event.keyCode === KEY_CODES.Enter && isIE11) {
      this.inputChange()
    }
  }

  /**
   * Обработка потери фокуса инпута.
   */
  handleFocusOut () {
    if (this.input) {
      const inputValue = this.input.value && Number(this.input.value())
      if (isIE11 && ((inputValue > 0) && inputValue !== Number(this.lastInputValue))) {
        this.inputChange()
      }
    }
  }

  /**
   * Ставит плейсхолдер в поле для кол-ва при наведении на кнопку "в корзину"
   */
  setPlaceholder () {
    let minQty = this.item.get('min_qty')
    const realMinQty = this.item.get('real_min_qty')
    if (this.item.get('has_custom_qty_units')) {
      minQty = this.convertQtyBasedOnNestedUnit(minQty, this.item.get('in_set'), 'to')
    }
    if (realMinQty) {
      minQty = realMinQty
    }
    this.input.attr('placeholder', this.getVisibleValue(minQty))
  }

  /**
   * Возвращается значение отображаемое в инпуте.
   * @param {number} value Исходное значение.
   * @returns {number} Отображаемое значение.
   */
  getVisibleValue (value) {
    return this.qtyIsWeight && isNumber(value)
      ? String(value / 10).replace('.', ',')
      : value
  }

  /**
   * Убирает плейсхолдер.
   */
  removePlaceholder () {
    this.input.removeAttr('placeholder')
  }

  /**
   * Инициализация ДОМ элементов
   */
  initDom () {
    this.$('.input__increase').hide()
    this.$('.input__decrease').hide()
    this.qtyBtn = this.$('.qty-btn')
    this.input = this.$('[data-cart-input]')
    this.btn = this.$('.button[data-action=add]')
    this.multiplyicity = this.$('[data-cart-k]').attr('data-cart-k') !== 'false'
    this.value = this.model.get('qty')
    this.lastInputValue = this.input.val()
  }

  /**
   * Обновление кнопок добавления товара
   * @param {boolean} [animate] Признак анимации.
   * @param {boolean} [tooltip] Признак тултипа.
   */
  updateControls (animate, tooltip) {
    let additionalText = ''
    const tooltipParams = [this.input, false]
    const value = this.input.val()
    this.updateTooltipType()
    // добавлено, чтобы позиция тултипов рассчитывалась, когда обновлена информация о корзине (SL-26567)
    const showTooltip = () => {
      this.listenToOnce(this.cart, 'sync', () => {
        this.tooltip.apply(this, tooltipParams)
      })
    }
    if (tooltip) {
      const additionalTextParams = {
        minimum: this.item.get('min_qty'),
        maximum: this.item.get('max_qty'),
        value: value,
        qtyMultiplier: this.item.get('qty_multiplier'),
        ending: getRussianDeclension(value, ['а', 'о', 'о']),
      }
      if (this.item.get('has_custom_qty_units')) {
        let inSet = this.item.get('in_set')
        Object.keys(additionalTextParams).map(key => {
          if (isNumber(additionalTextParams[key])) {
            additionalTextParams[key] = this.convertQtyBasedOnNestedUnit(additionalTextParams[key], inSet, 'to')
          }
        })
      }
      if (this.tooltipType) {
        switch (this.tooltipType) {
          case 'decrease':
            tooltipParams[1] = $(this.decrease_tooltip_template())
            break
          case 'minimum':
            additionalText = this.minimum_tooltip_text_template(additionalTextParams)
            break
          case 'maximum':
            tooltipParams[1] = $(this.maximum_tooltip_text_template(additionalTextParams))
            break
          case 'already_maximum':
            tooltipParams[1] = $(this.already_maximum_tooltip_text_template())
            break
          case 'multiplicity':
            additionalText = this.multiplicity_tooltip_text_template(additionalTextParams)
            break
          case 'null':
            tooltipParams[1] = $(this.null_tooltip_template())
            break
          case 'delete':
            tooltipParams[1] = $(this.delete_tooltip_template())
            break
        }
        this.tooltipType = false
      }
    }
    if (additionalText) {
      tooltipParams.push(additionalText)
    }

    // если товар не добавлен в корзину, пропускаем обновление кнопок +/-
    if (!isNumber(this.model.get('qty'))) {
      this.$el.removeClass('added_to_cart')
      if (tooltip) {
        showTooltip()
      }
      return
    }

    this.setSlider(0, this.MAX_QTY_IN_INPUT)
    this.btn.removeClass('button_blue').addClass('button_blue-clean')
    this.$el.addClass('added_to_cart')
    let $ctl1 = this.$('.ctl[data-action=remove]'),
      $ctl2 = this.$('.product__qt, .product_qt-label, .product__btn'),
      $btn_icon = this.btn.find('.with-icon')

    if (animate && this.btn && this.btn.length) {
      this.btn.addClass('animate')
      if (supportsCssProperty('animation') && !isMobile) {
        this.btn.one('animationend webkitAnimationEnd oAnimationEnd MSAnimationEnd', () => {
          clearTimeout(this.tt)
          this.tt = setTimeout(() => {
            this.btn.removeClass('animate')
            $ctl1.removeClass('not_display')
            if (tooltip) {
              showTooltip()
            }
          }, 300)
        })
      } else {
        clearTimeout(this.tt)
        this.btn.removeClass('animate')
        $ctl1.removeClass('not_display')
        if (tooltip) {
          showTooltip()
        }
      }
    } else {
      if ($ctl1.hasClass('not_display') && !$ctl2.hasClass('no-hide')) {
        $ctl1.removeClass('not_display')
        $ctl1.addClass('no-hide')
      } else if (!$ctl2.hasClass('no-hide')) {
        $ctl1.toggleClass('not_display')
      }
      if (tooltip) {
        showTooltip()
      }
    }

    if (!this.$el.hasClass('xxl-btn')) {
      $btn_icon.removeClass('with-icon_cart-white with-icon_cart-black').addClass('with-icon_plus')
    }

    if ($ctl2.hasClass('ctl-hidden') && !$ctl2.hasClass('no-hide')) {
      $ctl2.removeClass('ctl-hidden')
      $ctl2.addClass('no-hide')
    } else if (!$ctl2.hasClass('no-hide')) {
      $ctl2.toggleClass('ctl-hidden')
    }
  }

  /**
   * Повторно обновляем тип подсказки после ответа
   * в случае если товар имеет дополнительные ограничения по количеству.
   */
  updateTooltipType () {
    const restrictionQty = this.model.get('restriction_qty')
    if (!this.wasRestricted && restrictionQty) {
      if (this.input.val() >= restrictionQty) {
        this.tooltipType = 'maximum'
      }
    }
  }

  /**
   * Считает позицию хэдера. Нужно для дальнейшего пересчета положения тултипа "Добавлено в корзину"
   * @returns {number} вехний оффсет хедера, либо 0
   */
  calculateHeaderPosition () {
    const offset = $('header').offset()
    return offset ? offset.top : 0
  }

  /**
   * Показывает тултип
   * @param {jQuery} $input
   * @param {jQuery} $tooltip
   * @param {string} text
   */
  tooltip ($input, $tooltip, text) {
    let input_current = $input.filter(':visible'),
      body = $('body'),
      offset = input_current.offset(),
      unitId = this.model.get('unit_id') || this.item.get('unit_id'),
      headerPosition = this.calculateHeaderPosition()
    if (input_current.length > 0) {
      if (!$tooltip) {
        $tooltip = $(this.tooltip_template({
          count: $input.val(),
          ending: getRussianDeclension($input.val(), ['а', 'о', 'о'], unitId),
          additionalText: text,
        }))
      }

      if ($(window).width() <= 1185) {
        this.listenTo(Backbone, 'hideTooltip', () => {
          $tooltip.addClass('not_display')
        })
        if (this.checkPreviewOpen()) {
          $tooltip.addClass('not_display')
        }
      }
      $tooltip.addClass('b-tooltip').appendTo('body').hide()

      // расчитываем с дополнительный отступом между инпутом и тултипом - 10px.
      let tooltipTopPos = headerPosition > 0 ?
          offset.top - $tooltip.outerHeight() - 10 - headerPosition :
          offset.top - $tooltip.outerHeight() - 10,
        tooltipLeftPos = offset.left - $tooltip.outerWidth() / 2 + input_current.outerWidth() / 2,
        tooltipWidth = $tooltip.outerWidth()
      $tooltip.css({
        position: 'absolute',
        top: tooltipTopPos,
        left: tooltipLeftPos,
      })
      if (!isDesktop) {
        let windowWidth = $(window).width(),
          width = tooltipWidth
        tooltipLeftPos = tooltipLeftPos - body.position().left
        tooltipTopPos = tooltipTopPos - body.position().top
        // для мобильной версии сайта
        if (tooltipLeftPos < 0) {
          width = tooltipWidth + tooltipLeftPos * 2 - 20
          tooltipLeftPos = 20
        }
        if (windowWidth < tooltipLeftPos + tooltipWidth) {
          // устанавливаем ширину с дополнительным отступом 20px для исключения возможности дергания экрана.
          width = (windowWidth - input_current.outerWidth() / 2 - offset.left + body.position().left) * 2 - 20
        }
        $tooltip.css({
          position: 'absolute',
          top: tooltipTopPos,
          left: tooltipLeftPos,
          width: width,
        })
      }
      // после рендера, если тултип близко к краю экрана, он может изменить размеры и позицию
      if (tooltipWidth !== $tooltip.outerWidth()) {
        $tooltip.css({
          top: offset.top - $tooltip.outerHeight() - 10 - body.position().top,
          left: offset.left - body.position().left - $tooltip.outerWidth() / 2 + input_current.outerWidth() / 2,
        })
      }
      $tooltip.fadeToggle(600, function () {
        $('body').on('mouseleave', '.b-tooltip', function () {
          $(this).fadeOut(function () {
            $(this).remove()
          })
        })
        setTimeout($.proxy(function () {
          if ($(this).filter(':hover').length === 0) {
            $(this).fadeOut(function () {
              $(this).remove()
            })
          }
        }, this), 2000)
      })
    }
  }

  /**
   * Слайдер на поле количества, кнопки +/-
   * @param {number} min
   * @param {number} max
   */
  setSlider (min, max) {
    if (!this.input) {
      return
    }
    if (this.value <= min) {
      this.$('.input__decrease').hide()
    } else {
      this.$('.input__decrease').show()
    }
    let isZeroBalance = this.item.get('balance') === 0 && !this.item.get('isEnough')
    this.$('.input__increase').toggleClass('not_display', isZeroBalance)
    this.$('.input_amount').attr('disabled', isZeroBalance)
    if ((this.value >= this.item.get('max_qty')) || isZeroBalance) {
      this.$('.input__increase').hide()
      this.$('.catalog__item-qt').addClass('catalog__item-qt_max-qty')
    } else {
      this.$('.catalog__item-qt').removeClass('catalog__item-qt_max-qty')
      this.$('.input__increase').show()
    }
  }

  /**
   * Метод для изменения отображения количества товара в зависимости от количества в наборе.
   * @param {number} qty количество товара
   * @param {number} inSet количество товара в наборе
   * @param {string} direction направление конверсии
   * @returns {number} result
   */
  convertQtyBasedOnNestedUnit (qty, inSet, direction) {
    let result
    switch (direction) {
      case 'from':
        result = qty / inSet
        break
      case 'to':
        result = qty * inSet
        break
    }
    return result
  }

  /**
   * Обработка ввода количества в зависимости от минимальной суммы и кратности.
   * @param {(number|NaN)} value          Текущее значение инпута добавленных товаров - NaN если значение пустое.
   * @param {number}       multiAction    Номер действия (1 - добавление, 0 - изменение инпута, -1 - уменьшение).
   * @param {boolean}      isAddToCartBtn Признак того, что ввод происходит через кнопку добавления
   *                                      товаров (не через плюсик у инпута).
   * @returns {number}
   * @private
   */
  _filterInputValue (value, multiAction, isAddToCartBtn = false) {
    if (this.qtyIsWeight) {
      value = parseInt(value.replace(',', '.') * 10, 10)
    } else {
      value = parseInt(value, 10)
    }
    this.wasRestricted = Boolean(this.model.get('restriction_qty'))
    if (this.item.get('has_custom_qty_units')) {
      value = this.convertQtyBasedOnNestedUnit(value, this.item.get('in_set'), 'from')
    }
    let modelMinQty = this.item.get('min_qty'),
      modelRealMinQty = this.item.get('real_min_qty'),
      canAddByRealMinQty = this.MIN_QTY_RETAIL === modelMinQty
        && modelMinQty !== modelRealMinQty,
      needAddMoreMinQty = isAddToCartBtn && (value === 0 || isNaN(value)),
      minQty = canAddByRealMinQty && needAddMoreMinQty ? modelRealMinQty : modelMinQty,
      maxQty = this.model.get('restriction_qty') || this.item.get('max_qty'),
      qtyMultiplier = this.item.get('qty_multiplier')
    this.tooltipType = false
    if (value < this.model.get('qty') || multiAction === -1) {
      this.tooltipType = 'decrease'
    }
    if ((value === 0 && multiAction < 1) || (qtyMultiplier > 0 && value <= minQty && multiAction < 0)) {
      this.tooltipType = 'null'
      value = 0
    } else if (value < minQty || !$.isNumeric(value)) {
      if (value < minQty && multiAction !== 1) {
        this.tooltipType = 'minimum'
      }
      value = minQty
    } else if ($.isNumeric(qtyMultiplier) && qtyMultiplier > 0) {
      if (multiAction === 1) {
        value = Math.floor((value / qtyMultiplier) + 1) * qtyMultiplier
      } else {
        if (value % qtyMultiplier > 0 && multiAction === 0) {
          this.tooltipType = 'multiplicity'
        }
        value = Math.ceil((value / qtyMultiplier) + multiAction) * qtyMultiplier
      }
    }
    if (value >= maxQty) {
      if (this.model.get('qty') === maxQty) {
        this.tooltipType = 'already_maximum'
      } else {
        this.tooltipType = 'maximum'
        value = maxQty
      }
    }
    return value
  }

  /**
   * Onchange для ввода количества
   * @param {event} e - событие изменения инпута
   */
  inputChange (e) {
    if (this.input) {
      const inputValue = e ? $(e.target).val() : this.input.val()
      this.lastInputValue = this.input.val()
      this.value = this._filterInputValue(inputValue, 0)
      this.addToCartPrev()
    }
  }

  /**
   * Увеличивает количество товара в корзине
   * @param {Event} e Событие.
   */
  increaseQty (e) {
    if (!this.atAddToCartProcess) {
      // если это просто добавление в корзину, увеличивать не надо
      const $target = $(e.currentTarget),
        isAddToCartBtn = $target.hasClass(this.addToCartBtnClassName)
      this.value = this._filterInputValue(
        this.input.val(),
        1,
        isAddToCartBtn
      )
      this.addToCartPrev()
    }
  }

  /**
   * Определяет какое действие нужно выполнить - обновление количества к корзине или добавление товара в корзину
   */
  addToCartPrev () {
    // если товара ещё нет в корзине и не отправлен запрос на добавление
    if ($.isNumeric(this.model.get('qty'))) {
      this.updateQty(this.value)
      this.showFastDeliveryPopup()
    } else {
      this.listenToOnce(this.cart, 'add', this.showFastDeliveryPopup)
      this.addToCart(this.value)
      this.showRemotePopup()
    }
  }

  /**
   * Уменьшает количество товара в корзине
   */
  decreaseQty () {
    if (this.atAddToCartProcess) {
      return
    }
    this.value = this._filterInputValue(this.input.val(), -1)
    this.updateQty(this.value)
  }

  /**
   * Возвращает строку, описывающую правило заказа - по / от
   * @returns {string} qtyRule Частица для правила заказа.
   */
  getQtyRule () {
    let qtyRule = ''
    const rules = this.item.get('custom_qty_rules_data') || this.item.get('qty_rules_data')
    const qty = this.model.get('qty')
    const minQty = this.item.get('min_qty')
    if (rules) {
      qtyRule = (qty < minQty && rules.from) ? rules.from : rules.on
    }
    return qtyRule
  }

  /**
   * Удаляет товар из корзины.
   * @param {Event} event jQuery-объект события.
   */
  removeFromCart (event) {
    if (event instanceof $.Event) {
      event.stopPropagation()
    }
    this.tooltipType = 'delete'
    if (this.atAddToCartProcess) {
      return
    }

    this.addItemToCartAwaited()
    const modelBeforeRemove = this.model.toJSON()
    const url = isFunction(this.model.url) ? this.model.url() : this.model.url
    this.model.set('qty', 0)
    this.item.isSync = true
    this.atAddToCartProcess = true
    this.input.val(null)
    this.model.destroy({
      silent: false,
      url: to(url + this.model.get('id'), null),
      success: (model, data) => {
        this.cart.trigger('cart-item-refresh', model)
        // Игнорируем последующие обновления
        this.listenToOnce(this.cart, 'cart-update', () => {
          this.fetchRetailFeeInfo()
          this.updateCartInfo(modelBeforeRemove)
          if (data && data._meta && data._meta.gifts) {
            Backbone.trigger('gift-deleted-from-cart', data._meta.gifts)
          }
        })
      },
      error: () => {
        this.removeItemFromCartAwaited()
        this.atAddToCartProcess = false
      },
    })
  }

  /**
   * Запрашивает информацию о сборке.
   */
  fetchRetailFeeInfo () {
    const item = new Item({
      id: this.item.get('id'),
    })
    item.urlRoot = `${entryScriptName}/api/v3/item/`
    this.listenToOnce(item, 'sync', function (model) {
      Backbone.trigger('retail-info-received', model)
    })
    item.fetch({
      data: {
        fields: 'retail_fee_markup',
        expand: 'retail_fee_markup',
      },
    })
  }

  /**
   * Обработка успешного получения информации о сборке.
   * @param {Item} item модель товара с информацией о сборке.
   */
  onRetailFeeInfoReceived (item) {
    if (isEqual(this.item.get('id'), item.get('id'))) {
      this.item.set('retail_fee_markup', item.get('retail_fee_markup'))
      this.renderRetailFeeMarkup()
    }
  }

  /**
   * Обновляем данные корзины
   * @param {Object} modelBeforeRemove
   */
  updateCartInfo (modelBeforeRemove) {
    this.cart.trigger('updateQty')
    // Для правильного отображения количества товара в модификаторе при его смене
    Backbone.trigger('update-cart-item', this.model, 'delete')
    Backbone.trigger('changeQtyForOko', 'remove', modelBeforeRemove)
    this.removeItemFromCartAwaited()
  }

  /**
   * Добавляет товар в корзину
   * @param {number} value
   */
  addToCart (value) {
    let url = isFunction(this.model.url) ? this.model.url() : this.model.url
    if (this.atAddToCartProcess) {
      return
    }
    this.addItemToCartAwaited()
    this.atAddToCartProcess = true
    this.item.isSync = true
    this.model.set('item', null)
    this.cart.create({
      qty: value,
      item_id: this.item.get('id'),
    }, {
      silent: false,
      wait: true,
      method: 'POST',
      url: to(url, ['currency'], { expand: 'item.retail_fee_markup,wholesale' }),
      success: model => {
        Backbone.trigger('update-cart-item', model, 'create')
        // Для правильного отображения количества товара в модификаторе при его смене
        this.trigger('update-total-sum')
        Backbone.trigger('changeQtyForOko', 'add', model.toJSON(), {}, this.isInRecommendationsBlock)
        this.removeItemFromCartAwaited()
      },
      error: () => {
        this.atAddToCartProcess = false
        this.removeItemFromCartAwaited()
      },
    })
  }

  /**
   * Обновляет количество товара в корзине
   * @param {number} value
   */
  updateQty (value) {
    let prevModel = this.model.toJSON()
    if (this.atAddToCartProcess) {
      return
    }
    this.addItemToCartAwaited()
    this.atAddToCartProcess = true
    this.item.isSync = true
    this.model.set('item', null)
    this.model.save({
      qty: value,
    }, {
      method: 'PUT',
      success: (model, data) => {
        // Если мы в корзине - не нужно сохранять в модель ссылку на CartItem
        if (!this.isCart) {
          this.item.set('cart_item', model.toJSON())
        }
        Backbone.trigger('update-cart-item', model, 'update')
        // Для правильного отображения количества товара в модификаторе при его смене
        this.trigger('update-total-sum')
        this.cart.costModifiers = model.get('_cost_modifiers')
        this.cart.updateCartSummaryBlocks(model.get('cart_summary_blocks'))
        /*
         * используется кастомное событие вместо стандартного, потому что стандартное срабатывает дважды,
         * один раз при добавлении модели в коллекцию, второй — при возрате ответа от сервера
         */
        this.cart.trigger('updateQty')
        Backbone.trigger('changeQtyForOko', 'change', model.toJSON(), prevModel, this.isInRecommendationsBlock)
        if (this.isCart) {
          this.cart.gifts = model.get('gifts')
          this.cart.giftsForActions = model.get('_reserve')
        }
        if (model.get('_transit_settlements')) {
          this.cart.transit_settlements = model.get('_transit_settlements')
        }
        if (data && data._meta && data._meta.gifts) {
          Backbone.trigger('gift-deleted-from-cart', data._meta.gifts)
        }
        this.removeItemFromCartAwaited()
      },
      url: to(this.model.url + this.model.get('id'), ['currency'], (this.isCart ? {
        expand: `gifts,reserve,volume_discount,wholesale,gifts_discount,item.retail_fee_markup,large_family${jsonFeatures.is_cart_summary_blocks ? ',cart_summary_blocks' : ''}`,
      } : { expand: 'item.retail_fee_markup,wholesale' })),
      error: () => {
        this.atAddToCartProcess = false
        this.removeItemFromCartAwaited()
      },
    })
  }

  /**
   * Добавление товара в список ожидания корзины
   */
  addItemToCartAwaited () {
    this.cart.addItemIdToAwaited(this.item.get('id'))
  }

  /**
   * Удаление товара из списка ожидания корзины
   */
  removeItemFromCartAwaited () {
    // Сделано через таймаут, чтобы корзина разблокировалась только после отработки всех слушателей
    setTimeout(() => {
      this.cart.removeItemIdFromAwaited(this.item.get('id'))
    }, 0)
  }

  /**
   * Показать попап возможности заказа с удаленного склада
   * (если товар с удаленного склада и попап еще не показывался)
   */
  showRemotePopup () {
    if (this.settlement && this.settlement.isRemoteStoreEnabled()) {
      if (this.item.get('is_remote_store') && !getCookie('isRemotePopupShown')) {
        Backbone.trigger(
          'show-remote-popup',
          this.item,
          this.model.get('qty')
        )
      }
    }
  }

  /**
   * Показать попап недоступности быстрой доставки
   * (если количество товаров в корзине превысило баланс быстрой доставки)
   *
   * @param {CartItem} newModel Модель созданного товара в корзине (передается при первом добавлении товара)
   */
  showFastDeliveryPopup (newModel) {
    const fastDeliveryBalance = this.item.get('fast_delivery_balance'),
      qty = newModel ? newModel.get('qty') : this.model.get('qty'),
      previousQty = newModel ? 0 : this.model.previousAttributes().qty,
      /*
       * ищем внутри элемента обертку для поля с количеством,
       * так как на разных страницах само поле имеет разные селекторы
       */
      positioningElement = this.$('.slider-amount-wrap')

    if (fastDeliveryBalance) {
      const isQtyMoreThanBalance = fastDeliveryBalance < qty,
        isPreviousQtyLessThanBalance = fastDeliveryBalance >= previousQty

      if (isQtyMoreThanBalance && isPreviousQtyLessThanBalance) {
        Backbone.trigger(
          'show-fast-delivery-popup',
          this.item,
          positioningElement,
        )
      }
    }
  }

  /**
   * Проверяет есть ли открытые превью в каталоге
   * @returns {boolean} надо ли скрывать tooltip
   */
  checkPreviewOpen () {
    let hideTooltip = false
    $('.hint-preview').each((index, element) => {
      if ($(element).css('display') === 'block') {
        hideTooltip = true
      }
    })
    return hideTooltip
  }
}
